// src/services/apiClient.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { handleApiError, ServiceError, logError } from './utils/apiErrorUtils';

// Extend the axios config to include our custom metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime?: number;
      requestId?: string;
      _retryCount?: number;
    };
    _retryCount?: number;
  }
}

// Centralized API base URL configuration
// Use relative URLs to leverage nginx proxy in both development and production


// Global error notification callback - can be set by the application
let globalErrorHandler: ((error: ServiceError) => void) | null = null;

export const setGlobalErrorHandler = (handler: (error: ServiceError) => void) => {
  globalErrorHandler = handler;
};

// Create a shared authenticated API client
// apiClient.ts
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').trim(); // '' or '/api'
const useCreds = ['true','1','yes'].includes(String(import.meta.env.VITE_USE_CREDENTIALS||'').toLowerCase());

// Safety guard against http on https pages
if (API_BASE_URL.startsWith('http://') && window.location.protocol === 'https:') {
  throw new Error(`Refusing insecure API over http from https page: ${API_BASE_URL}`);
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL || '',      // e.g. '/api'
  withCredentials: useCreds,
  // let Axios set Content-Type only when needed to reduce preflights
});

// CSRF token management
let csrfToken: string | null = null;

// Function to get CSRF token from cookies
const getCSRFToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  
  const name = 'csrftoken';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

// Function to check if user is authenticated via cookies
export const isAuthenticatedViaCookies = (): boolean => {
  if (typeof document === 'undefined') return false;
  
  const cookies = document.cookie.split(';');
  return cookies.some(cookie => cookie.trim().startsWith('access_token='));
};

// Request interceptor to add CSRF token and request ID
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add CSRF token for state-changing requests only when:
    // 1. Making same-origin requests, OR
    // 2. withCredentials is enabled (indicating secure cookie auth)
    // 3. NOT an auth endpoint (auth endpoints use @csrf_exempt)
    const token = getCSRFToken() || csrfToken;
    const isStateChangingRequest = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '');
    const shouldSendCSRF = config.withCredentials || !config.baseURL || config.baseURL === '' || config.baseURL === window.location.origin;
    const isAuthEndpoint = config.url?.includes('/api/auth/');
    
    if (token && isStateChangingRequest && shouldSendCSRF && !isAuthEndpoint) {
      config.headers['X-CSRFToken'] = token;
    }

    // Also add Authorization header if we have an access token (fallback)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Generate request ID for internal tracking (don't send as header to avoid CORS issues)
    const requestId = crypto.randomUUID();
    
    // Add timestamp for request timing
    config.metadata = { 
      ...config.metadata,
      startTime: Date.now(),
      requestId: requestId
    };

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(handleApiError(error));
  }
);

// Response interceptor to handle common errors and retries
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful requests in development
    if (process.env.NODE_ENV === 'production') {
      const duration = Date.now() - (response.config.metadata?.startTime || 0);
      console.log(
        `✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} (${duration}ms)`
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config;
    const requestId = config?.metadata?.requestId || 'unknown';
    
    // Log error details
    if (config?.metadata?.startTime) {
      const duration = Date.now() - config.metadata.startTime;
      console.error(
        `❌ ${config.method?.toUpperCase()} ${config.url} - ${error.response?.status || 'NETWORK_ERROR'} (${duration}ms) [${requestId}]`
      );
    }

    // Handle different types of errors
    let serviceError: ServiceError;

    try {
      // Handle rate limiting with smart retry for extraction endpoints
      if (error.response?.status === 429) {
        const retryAfter = parseInt(error.response.headers['retry-after'] || '60');
        
        // For extraction endpoints, implement retry with exponential backoff
        if (config?.url?.includes('/etl/extract-data/') || config?.url?.includes('extract')) {
          config._retryCount = config._retryCount || 0;
          
          if (config._retryCount < 3) {
            config._retryCount += 1;
            // Exponential backoff: retry after specified time * retry attempt, max 3 minutes
            const delayMs = Math.min(retryAfter * 1000 * config._retryCount, 180000);
            
            console.log(`🔄 Rate limited during extraction. Retrying after ${delayMs/1000}s (attempt ${config._retryCount}/3) [${requestId}]`);
            
            // Wait for the specified delay
            await new Promise(resolve => setTimeout(resolve, delayMs));
            
            // Retry the request
            return apiClient(config);
          } else {
            console.warn(`🚫 Max retries reached for extraction endpoint [${requestId}]`);
          }
        }
        
        serviceError = new ServiceError(
          `Too many requests. Please try again in ${retryAfter} seconds.`,
          429,
          error
        );
      }
      // Handle timeout errors specifically for extraction
      else if (error.code === 'ECONNABORTED' && config?.url?.includes('/etl/extract-data/')) {
        console.error(`🕒 Extraction request timed out after 5 minutes [${requestId}]`);
        
        serviceError = new ServiceError(
          'The data extraction is taking longer than expected. This might be due to rate limiting on the source platform. Please try again in a few minutes.',
          408,
          error
        );
      }
      // Handle authentication errors
      else if (error.response?.status === 401) {
        console.warn(`🔐 Authentication failed [${requestId}]`);
        
        // Check if this is a login attempt vs authenticated request
        const isLoginRequest = config?.url?.includes('/api/auth/login');
        const is2FARequest = config?.url?.includes('/api/auth/2fa/verify');
        
        let errorMessage = 'Your session has expired. Please log in again.';
        let shouldRedirect = true;
        
        if (isLoginRequest || is2FARequest) {
          // For login attempts, always show generic message to prevent username enumeration
          errorMessage = 'Invalid email or password. Please try again.';
          shouldRedirect = false; // Don't redirect during login
        }
        
        serviceError = new ServiceError(
          errorMessage,
          401,
          error
        );

        // Only redirect if not on auth page and not a login attempt
        if (shouldRedirect && !window.location.pathname.includes('/auth')) {
          setTimeout(() => {
            window.location.href = '/auth';
          }, 1000); // Give time for error message to show
        }
      }
      // Handle forbidden errors
      else if (error.response?.status === 403) {
        serviceError = new ServiceError(
          'You do not have permission to perform this action.',
          403,
          error
        );
      }
      // Handle not found errors
      else if (error.response?.status === 404) {
        serviceError = new ServiceError(
          'The requested resource was not found.',
          404,
          error
        );
      }
      // Handle server errors
      else if (error.response?.status && error.response.status >= 500) {
        serviceError = new ServiceError(
          'A server error occurred. Please try again later.',
          error.response.status,
          error
        );
      }
      // Handle network errors
      else if (!error.response) {
        serviceError = new ServiceError(
          'Network error. Please check your internet connection.',
          0,
          error
        );
      }
      // Handle all other errors using the utility function
      else {
        serviceError = handleApiError(error);
      }

      // Log the error
      logError(serviceError, `API Request [${requestId}]`);

      // Call global error handler if set (for notifications)
      if (globalErrorHandler && serviceError.statusCode !== 401) {
        // Don't show global notifications for auth errors as they redirect
        globalErrorHandler(serviceError);
      }

    } catch (processingError) {
      // If error processing fails, create a generic error
      serviceError = new ServiceError(
        'An unexpected error occurred while processing the request.',
        error.response?.status,
        error
      );
      console.error('Error processing failed:', processingError);
    }

    return Promise.reject(serviceError);
  }
);


export default apiClient;